import React, { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import { authApi } from 'api/auth';
import { getCurrentUser } from 'api/client';

const NAV_ITEMS = [
  {
    label: 'Today',
    path: '/today',
    icon: 'Sun',
    description: 'Your daily workflow',
  },
  {
    label: 'Executive Overview',
    path: '/executive-overview',
    icon: 'LayoutDashboard',
    description: 'Strategic KPIs and revenue analytics',
  },
  {
    label: 'Sales Performance',
    path: '/sales-performance',
    icon: 'TrendingUp',
    description: 'Pipeline analysis and team performance',
  },
  {
    label: 'Customer Analytics',
    path: '/customer-analytics',
    icon: 'Users',
    description: 'Behavioral analysis and retention insights',
  },
  {
    label: 'Leads',
    path: '/leads',
    icon: 'UserCheck',
    description: 'Lead tracking and qualification',
  },
  {
    label: 'Contacts',
    path: '/contacts',
    icon: 'BookUser',
    description: 'People in your CRM',
  },
  {
    label: 'Inbox',
    path: '/inbox',
    icon: 'MessageSquare',
    description: 'WhatsApp conversations',
  },
  {
    label: 'Tasks',
    path: '/tasks',
    icon: 'CheckSquare',
    description: 'Follow-ups and to-dos',
  },
  {
    label: 'Approvals',
    path: '/approvals',
    icon: 'ShieldCheck',
    description: 'AI suggestions + follow-up drafts',
  },
  {
    label: 'Sales Pipeline',
    path: '/sales-pipeline',
    icon: 'Kanban',
    description: 'Visual pipeline management',
  },
  {
    label: 'Opportunities',
    path: '/opportunities',
    icon: 'Target',
    description: 'Deal tracking and opportunity management',
  },
  {
    label: 'Pilot Overview',
    path: '/pilot',
    icon: 'Rocket',
    description: 'Pilot ops + north-star funnel (Owner)',
    roles: ['Owner'],
  },
  {
    label: 'Team',
    path: '/team',
    icon: 'Users',
    description: 'Invite & manage members (Owner / Manager)',
    roles: ['Owner', 'Manager'],
  },
  {
    label: 'Activity',
    path: '/activity',
    icon: 'History',
    description: 'Org-wide audit trail + undo',
  },
  {
    label: 'Settings',
    path: '/settings',
    icon: 'Settings',
    description: 'Workspace, WhatsApp channel + AI budget (Owner)',
    roles: ['Owner', 'Manager'],
  },
  {
    label: 'Operations',
    path: '/operations',
    icon: 'Gauge',
    description: 'Reply times, auto-replies, escalations, AI tokens',
  },
  {
    label: 'Invoices',
    path: '/invoices',
    icon: 'Receipt',
    description: 'Invoice management — amounts, tax, status',
  },
  {
    label: 'Integrations',
    path: '/integrations',
    icon: 'Plug',
    description: 'Website form, IndiaMART, WhatsApp — every lead source',
    roles: ['Owner', 'Manager'],
  },
];

export default function Sidebar({ collapsed, onToggle }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const currentUser = getCurrentUser();
  const fullName = currentUser?.fullName || 'User';
  const role = currentUser?.role || 'User';
  const initials = currentUser?.initials || 'U';

  useEffect(() => {
    setMobileOpen(false);
  }, [location?.pathname]);

  const isCollapsed = collapsed;

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile toggle button */}
      <button
        className="lg:hidden fixed top-3 left-3 z-50 flex items-center justify-center rounded-md transition-all duration-200"
        style={{
          width: '40px',
          height: '40px',
          background: 'var(--color-card)',
          border: '1px solid var(--color-border)',
          color: 'var(--color-foreground)',
          boxShadow: 'var(--shadow-md)',
        }}
        onClick={() => setMobileOpen((p) => !p)}
        aria-label="Toggle navigation"
      >
        <Icon name={mobileOpen ? 'X' : 'Menu'} size={18} color="currentColor" />
      </button>

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full z-40 flex flex-col transition-all duration-300 ease-in-out
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${isCollapsed ? 'lg:w-16' : 'lg:w-60'}
        `}
        style={{
          background: 'var(--color-card)',
          borderRight: '1px solid var(--color-border)',
          boxShadow: 'var(--shadow-lg)',
        }}
      >
        {/* Logo */}
        <div
          className="flex items-center gap-3 px-4 flex-shrink-0"
          style={{ height: '64px', borderBottom: '1px solid var(--color-border)' }}
        >
          <div
            className="flex items-center justify-center rounded-lg flex-shrink-0"
            style={{
              width: '36px',
              height: '36px',
              background: 'var(--color-primary)',
              minWidth: '36px',
            }}
          >
            <Icon name="BarChart3" size={18} color="#FFFFFF" strokeWidth={2} />
          </div>
          {(!isCollapsed || mobileOpen) && (
            <span
              className="font-heading font-bold text-base truncate"
              style={{ color: 'var(--color-foreground)', letterSpacing: '-0.02em' }}
            >
              CRM<span style={{ color: 'var(--color-primary)' }}>Analytics</span>
            </span>
          )}
        </div>

        {/* Nav Items */}
        <nav className="flex-1 overflow-y-auto py-3 px-2">
          {NAV_ITEMS?.filter((item) => !item.roles || item.roles.includes(role)).map((item) => {
            const isActive = location?.pathname === item?.path;
            return (
              <NavLink
                key={item?.path}
                to={item?.path}
                title={isCollapsed && !mobileOpen ? item?.label : undefined}
                className="flex items-center gap-3 rounded-lg mb-1 transition-all duration-150 group relative"
                style={{
                  padding: isCollapsed && !mobileOpen ? '10px 12px' : '10px 12px',
                  background: isActive ? 'var(--color-primary)' : 'transparent',
                  color: isActive ? '#FFFFFF' : 'var(--color-text-secondary)',
                  minHeight: '44px',
                  justifyContent: isCollapsed && !mobileOpen ? 'center' : 'flex-start',
                }}
                onMouseEnter={(e) => {
                  if (!isActive) e.currentTarget.style.background = 'var(--color-muted)';
                }}
                onMouseLeave={(e) => {
                  if (!isActive) e.currentTarget.style.background = 'transparent';
                }}
              >
                <Icon
                  name={item?.icon}
                  size={18}
                  color={isActive ? '#FFFFFF' : 'currentColor'}
                  strokeWidth={isActive ? 2.5 : 2}
                  className="flex-shrink-0"
                />
                {(!isCollapsed || mobileOpen) && (
                  <span
                    className="font-caption font-medium text-sm truncate"
                    style={{ color: isActive ? '#FFFFFF' : 'var(--color-foreground)' }}
                  >
                    {item?.label}
                  </span>
                )}
                {/* Tooltip for collapsed state */}
                {isCollapsed && !mobileOpen && (
                  <div
                    className="absolute left-full ml-2 px-2 py-1 rounded-md text-xs font-caption font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-150 z-50"
                    style={{
                      background: 'var(--color-foreground)',
                      color: 'var(--color-background)',
                      boxShadow: 'var(--shadow-md)',
                    }}
                  >
                    {item?.label}
                  </div>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Bottom section */}
        <div style={{ borderTop: '1px solid var(--color-border)', padding: '12px 8px' }}>
          {/* User info */}
          <button
            onClick={() => navigate('/settings')}
            className="flex items-center gap-3 rounded-lg px-3 py-2 mb-2 w-full cursor-pointer transition-all duration-150 hover:bg-muted text-left"
            style={{ justifyContent: isCollapsed && !mobileOpen ? 'center' : 'flex-start' }}
            title="Workspace settings"
          >
            <div
              className="flex items-center justify-center rounded-full text-xs font-medium font-caption flex-shrink-0"
              style={{
                width: '32px',
                height: '32px',
                background: 'var(--color-primary)',
                color: '#FFFFFF',
                minWidth: '32px',
              }}
            >
              {initials}
            </div>
            {(!isCollapsed || mobileOpen) && (
              <div className="flex flex-col min-w-0">
                <span className="font-caption font-medium text-sm truncate" style={{ color: 'var(--color-foreground)' }}>
                  {fullName}
                </span>
                <span className="font-caption text-xs truncate" style={{ color: 'var(--color-text-secondary)' }}>
                  {role}
                </span>
              </div>
            )}
          </button>

          {/* Logout */}
          <button
            className="flex items-center gap-3 rounded-lg px-3 py-2 w-full transition-all duration-150 hover:bg-muted"
            style={{
              color: 'var(--color-text-secondary)',
              justifyContent: isCollapsed && !mobileOpen ? 'center' : 'flex-start',
            }}
            onClick={() => {
              authApi.logout().finally(() => navigate('/login'));
            }}
            title={isCollapsed && !mobileOpen ? 'Logout' : undefined}
          >
            <Icon name="LogOut" size={16} color="currentColor" className="flex-shrink-0" />
            {(!isCollapsed || mobileOpen) && (
              <span className="font-caption text-sm">Logout</span>
            )}
          </button>

          {/* Collapse toggle - desktop only */}
          <button
            className="hidden lg:flex items-center gap-3 rounded-lg px-3 py-2 w-full mt-1 transition-all duration-150 hover:bg-muted"
            style={{
              color: 'var(--color-text-secondary)',
              justifyContent: isCollapsed ? 'center' : 'flex-start',
            }}
            onClick={onToggle}
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <Icon name={isCollapsed ? 'ChevronRight' : 'ChevronLeft'} size={16} color="currentColor" />
            {!isCollapsed && (
              <span className="font-caption text-sm">Collapse</span>
            )}
          </button>
        </div>
      </aside>
    </>
  );
}
